import { GROUP_COOKIE_SEPARATOR, getTenantGroupKey } from './cacheKey'
import { COHORTS } from './utils'

/**
 * The ConfigCat text flag that says which groups are server rendered. It is
 * read on the server by the gate and in the browser by the cookie writer, so
 * a group that would not render never writes a cookie and never becomes a
 * cache key at the edge.
 */
export const SSR_ALLOW_LIST_FLAG = 'ssrHomeCohorts'

/** The flag value that means nobody, kept readable in the ConfigCat UI. */
export const SSR_ALLOW_LIST_OFF = 'off'

/** Matches any value of a token. */
export const ANY_TOKEN = '*'

/** Separates alternatives inside one token, `5|6|8`. */
export const TOKEN_ALTERNATIVE_SEPARATOR = '|'

/**
 * One allow list entry, decomposed: one set of accepted values per token of
 * the group cookie, in cookie order. An empty set means any value.
 */
export type GroupPattern = ReadonlyArray<ReadonlySet<string>>

/**
 * Parses the flag value. Entries are comma separated. Each entry is either a
 * bare cohort, which means that cohort in any shape, or a full pattern with
 * one token per cookie token where `*` matches anything and `|` separates
 * alternatives:
 *
 *     alfa, india
 *     alfa|india.d.*.*.*.5|6|8.en-US|fr-FR|de-DE
 *
 * The cohort token is the only one that may not be `*`, so a typo can never
 * open the render to everyone. Cohorts that the tenant does not know, entries
 * with the wrong number of tokens, entries with an empty token (`alfa..s`, a
 * stray `|`), `off` and anything else are dropped, and a flag that parses to
 * nothing means nobody is server rendered.
 */
export function parseSsrAllowList(
  tenant: string,
  value: unknown
): GroupPattern[] {
  if (typeof value !== 'string') return []
  const groupKey = getTenantGroupKey(tenant)
  if (!groupKey) return []

  const patterns: GroupPattern[] = []
  for (const rawEntry of value.split(',')) {
    const entry = rawEntry.trim()
    if (!entry || entry.toLowerCase() === SSR_ALLOW_LIST_OFF) continue
    const tokens = entry.split(GROUP_COOKIE_SEPARATOR)
    if (tokens.length !== 1 && tokens.length !== groupKey.attributes.length) {
      continue
    }
    const pattern: Set<string>[] = []
    for (const [position, attribute] of groupKey.attributes.entries()) {
      const token = tokens[position] ?? ANY_TOKEN
      if (token === ANY_TOKEN) {
        pattern.push(new Set<string>())
        continue
      }
      const alternatives = token
        .split(TOKEN_ALTERNATIVE_SEPARATOR)
        .map((alternative) => alternative.trim())
      // Only `*` may mean any value. An empty token, or an empty alternative
      // next to a `|`, is a typo and must not widen the entry.
      if (alternatives.some((alternative) => alternative === '')) break
      pattern.push(
        attribute === 'cohort'
          ? new Set(
              alternatives
                .map((cohort) => cohort.toLowerCase())
                .filter((cohort) => COHORTS.includes(cohort))
            )
          : new Set(alternatives)
      )
    }
    // Short when a token was empty. A cohort set that is empty here is either
    // `*` or nothing but unknown names. None of these may render.
    if (pattern.length !== groupKey.attributes.length) continue
    if (pattern[0]?.size === 0) continue
    patterns.push(pattern)
  }
  return patterns
}

/**
 * True when the cookie value matches at least one pattern. The cookie is
 * compared token by token against the parsed patterns, so both sides of the
 * wire decide the same way from the same string.
 */
export function isGroupAllowed(
  tenant: string,
  cookieValue: string,
  patterns: readonly GroupPattern[]
): boolean {
  const groupKey = getTenantGroupKey(tenant)
  if (!groupKey) return false
  const tokens = cookieValue.split(GROUP_COOKIE_SEPARATOR)
  if (tokens.length !== groupKey.attributes.length) return false
  return patterns.some((pattern) =>
    pattern.every((accepted, position) => {
      const token = tokens[position] ?? ''
      return accepted.size === 0 ? token !== '' : accepted.has(token)
    })
  )
}

/** The cohorts that appear anywhere in the list, for logs and tests. */
export function allowedCohorts(patterns: readonly GroupPattern[]): string[] {
  const cohorts = new Set<string>()
  for (const pattern of patterns) {
    for (const cohort of pattern[0] ?? []) cohorts.add(cohort)
  }
  return [...cohorts].sort()
}
